import { useLayoutEffect, useState, type RefObject } from 'react';
import type { BranchConnection } from './connectors';
import { CONNECTOR_GUTTER_CURVE, CONNECTOR_MIN_CURVE } from './constants';

export interface BranchConnectorPath {
  id: string;
  d: string;
}

const escapeId = (id: string): string =>
  typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(id) : id;

const nodeSelector = (id: string): string => `[data-node-id="${escapeId(id)}"]`;
const connectorFromSelector = (id: string): string => `[data-connector-from="${escapeId(id)}"]`;
const connectorToSelector = (id: string): string => `[data-connector-to="${escapeId(id)}"]`;

// A node's vertical extent in container-relative coords.
interface NodeRect {
  top: number;
  bottom: number;
  center: number;
}

// Lowest bottom / highest top among the cards sharing a row with the band
// [top, bottom] (matched by their centre falling inside it).
const rowBottom = (nodeRects: NodeRect[], top: number, bottom: number): number =>
  nodeRects.reduce((m, r) => (r.center >= top && r.center <= bottom ? Math.max(m, r.bottom) : m), bottom);
const rowTop = (nodeRects: NodeRect[], top: number, bottom: number): number =>
  nodeRects.reduce((m, r) => (r.center >= top && r.center <= bottom ? Math.min(m, r.top) : m), top);

// Measures the rendered position of each connection's endpoints (relative to
// the container) and produces a smooth cubic-bezier path with horizontal
// tangents, so a wrapped node links back to its predecessor/successor with a
// diagonal that reads as a continuation of the flow. Re-measures on layout
// changes (connection set) and on container resize / wrapping.
export function useBranchConnectorPaths(
  containerRef: RefObject<HTMLElement | null>,
  connections: BranchConnection[]
): BranchConnectorPath[] {
  const [paths, setPaths] = useState<BranchConnectorPath[]>([]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) {
      setPaths([]);
      // Return a value (matching the cleanup return below) so the effect
      // callback is consistent about always specifying a return value.
      return undefined;
    }

    const measure = (): void => {
      const containerRect = container.getBoundingClientRect();
      const next: BranchConnectorPath[] = [];

      // Every node's vertical extent in container-relative coords. Cards in the
      // same row can differ in height (those with dependency tags are taller),
      // so a gutter line must clear the tallest card in the row, not just the
      // card it starts/ends on.
      const nodeRects = Array.from(container.querySelectorAll('[data-node-id]')).map(
        (el): NodeRect => {
          const rect = el.getBoundingClientRect();
          return {
            top: rect.top - containerRect.top,
            bottom: rect.bottom - containerRect.top,
            center: rect.top + rect.height / 2 - containerRect.top
          };
        }
      );

      // Vertical centre of a node's horizontal connector, so the diagonal
      // starts and ends at the very same point as the straight "after" lines.
      // The connector element is a thin, vertically-centred rail, so its own
      // centre is the line. Falls back to the node centre when there is no
      // connector (e.g. the first node of a branch has no incoming line).
      const connectorY = (selector: string, fallback: number): number => {
        const el = container.querySelector(selector);
        if (!el) return fallback;
        const rect = el.getBoundingClientRect();
        return rect.top + rect.height / 2 - containerRect.top;
      };

      for (const { fromId, toId } of connections) {
        const fromEl = container.querySelector(nodeSelector(fromId));
        const toEl = container.querySelector(nodeSelector(toId));
        if (!fromEl || !toEl) continue;

        const from = fromEl.getBoundingClientRect();
        const to = toEl.getBoundingClientRect();

        const x1 = from.right - containerRect.left;
        const y1 = connectorY(connectorFromSelector(fromId), from.top + from.height / 2 - containerRect.top);
        const x2 = to.left - containerRect.left;
        const y2 = connectorY(connectorToSelector(toId), to.top + to.height / 2 - containerRect.top);

        const fromTop = from.top - containerRect.top;
        const fromBottom = from.bottom - containerRect.top;
        const toTop = to.top - containerRect.top;
        const toBottom = to.bottom - containerRect.top;
        const differentRows = fromBottom <= toTop + 1 || toBottom <= fromTop + 1;

        // Control-point offsets follow the link's horizontal direction so a
        // backward (right-to-left) link curves inward instead of overshooting
        // past both endpoints and across the cards between them.
        const dir = Math.sign(x2 - x1) || 1;

        let pathData: string;
        if (differentRows) {
          // Route through the empty gutter between the two rows: hug the gutter
          // across the span, then rise into each endpoint over a short distance
          // so the line never crosses a card sitting between the columns. The
          // gutter is measured from the whole upper row's lowest edge to the
          // lower row's highest edge, so a short endpoint card (e.g. one without
          // a dependency tag) can't pull the line up into a taller neighbour.
          const fromIsUpper = fromTop < toTop;
          const upperBottom = fromIsUpper
            ? rowBottom(nodeRects, fromTop, fromBottom)
            : rowBottom(nodeRects, toTop, toBottom);
          const lowerTop = fromIsUpper
            ? rowTop(nodeRects, toTop, toBottom)
            : rowTop(nodeRects, fromTop, fromBottom);
          const gutterY = (upperBottom + lowerTop) / 2;
          const curve = Math.min(CONNECTOR_GUTTER_CURVE, Math.abs(x2 - x1) * 0.5);
          pathData = `M ${x1} ${y1} C ${x1 + dir * curve} ${gutterY}, ${x2 - dir * curve} ${gutterY}, ${x2} ${y2}`;
        } else {
          const curve = Math.max(CONNECTOR_MIN_CURVE, Math.abs(x2 - x1) * 0.5);
          pathData = `M ${x1} ${y1} C ${x1 + dir * curve} ${y1}, ${x2 - dir * curve} ${y2}, ${x2} ${y2}`;
        }
        next.push({ id: `${fromId}->${toId}`, d: pathData });
      }

      setPaths(next);
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => {
      observer.disconnect();
    };
  }, [containerRef, connections]);

  return paths;
}
